import { useState, useMemo, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Message } from '@/lib/types'
import { User, Bot, File, Download, Copy, Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import 'prism-themes/themes/prism-vsc-dark-plus.css'
import { parseCodeBlocks, extractAllCodeBlocks, type CodeBlock } from '@/lib/code-parser'
import { 
  parseStreamingContent, 
  createStreamingParserState, 
  type StreamingParserState 
} from '@/lib/streaming-code-parser'
import { configService } from '@/lib/config-service'
import { apiClient } from '@/lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { ThinkingPanel } from '@/components/ThinkingPanel'

function SecureGeneratedImage({ src, alt }: { src: string; alt?: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let revokedUrl: string | null = null
    setObjectUrl(null)
    setError(null)

    const run = async () => {
      try {
        const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000'
        const token = apiClient.getAuthToken()
        const headers: HeadersInit = {}
        if (token) headers.Authorization = `Bearer ${token}`

        // Always load generated files from backend base URL.
        // In production, frontend may not proxy `/api` to backend, so absolute frontend URLs can break image rendering.
        const match = src.match(/\/api\/files\/generated\/([^/?#]+)/)
        const downloadId = match?.[1]
        const url = downloadId ? `${apiBaseUrl}/api/files/generated/${downloadId}` : (src.startsWith('http') ? src : `${apiBaseUrl}${src}`)

        const resp = await fetch(url, { headers })
        if (!resp.ok) {
          if (resp.status === 401) throw new Error('Authentication required. Please login again.')
          throw new Error(`Failed to load image: ${resp.statusText}`)
        }
        const blob = await resp.blob()
        const obj = window.URL.createObjectURL(blob)
        revokedUrl = obj
        setObjectUrl(obj)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load image')
      }
    }

    void run()

    return () => {
      if (revokedUrl) window.URL.revokeObjectURL(revokedUrl)
    }
  }, [src])

  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    )
  }

  if (!objectUrl) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Loading image...
      </div>
    )
  }

  return <img src={objectUrl} alt={alt || 'generated image'} className="max-w-full rounded-lg border border-gray-200 dark:border-gray-700" />
}

function MarkdownImg({ src, alt, ...props }: any) {
  const s = String(src || '')
  const isGenerated = s.includes('/api/files/generated/')
  if (isGenerated) {
    return <SecureGeneratedImage src={s} alt={alt} />
  }
  return <img src={s} alt={alt} {...props} />
}

interface MessageItemProps {
  message: Message
  isStreaming?: boolean
}

interface CodeBlockProps {
  children: string
  className?: string
  inline?: boolean
  language?: string
  isCustomDelimited?: boolean
  isIncomplete?: boolean
  [key: string]: any
}

function EnhancedCodeBlock({ 
  children, 
  className, 
  inline, 
  language: propLanguage,
  isCustomDelimited = false,
  isIncomplete = false
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false)
  const language = propLanguage || className?.replace('language-', '') || 'text'
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(children)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // Silently fail clipboard operation
    }
  }

  if (inline) {
    return (
      <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-emerald-600 dark:text-emerald-400">
        {children}
      </code>
    )
  }

  return (
    <div className="relative group">
      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-900 text-gray-300 px-4 py-2 text-sm rounded-t-lg">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase font-medium">{language}</span>
          {isCustomDelimited && (
            <span className="text-xs px-1.5 py-0.5 bg-emerald-600 text-white rounded">
              ENHANCED
            </span>
          )}
          {isIncomplete && (
            <span className="text-xs px-1.5 py-0.5 bg-yellow-600 text-white rounded animate-pulse">
              STREAMING...
            </span>
          )}
        </div>
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 transition-colors opacity-0 group-hover:opacity-100"
          title="Copy code to clipboard"
        >
          {copied ? (
            <><Check className="w-3 h-3" /> Copied</>
          ) : (
            <><Copy className="w-3 h-3" /> Copy</>
          )}
        </button>
      </div>
      <pre className={`bg-gray-900 dark:bg-black text-gray-100 p-4 overflow-auto rounded-b-lg border-t border-gray-700 max-h-96 scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500 ${
        isIncomplete ? 'border-l-4 border-l-yellow-500' : isCustomDelimited ? 'border-l-4 border-l-emerald-500' : ''
      }`}>
        <code className="font-mono text-sm leading-relaxed">{children}</code>
        {isIncomplete && (
          <span className="inline-block w-2 h-4 bg-yellow-500 animate-pulse ml-1 align-middle" />
        )}
      </pre>
    </div>
  )
}

// Legacy CodeBlock for backward compatibility
function CodeBlock({ children, className, inline }: CodeBlockProps) {
  return (
    <EnhancedCodeBlock
      children={children}
      className={className}
      inline={inline}
      isCustomDelimited={false}
    />
  )
}

export function MessageItem({ message, isStreaming }: MessageItemProps) {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const isTool = message.role === 'tool'
  const isSystem = message.role === 'system'


  // Streaming parser state for real-time code detection
  const streamingParserStateRef = useRef<StreamingParserState | null>(null)
  const previousContentRef = useRef<string>('')
  const [customDelimitersEnabled, setCustomDelimitersEnabled] = useState<boolean | null>(null)

  // Quick copy for whole message content
  const [copiedMessage, setCopiedMessage] = useState(false)

  const handleCopyMessage = async () => {
    if (!message.content) return
    try {
      await navigator.clipboard.writeText(message.content)
      setCopiedMessage(true)
      setTimeout(() => setCopiedMessage(false), 2000)
    } catch (err) {
      // Silently ignore clipboard errors
    }
  }

  // Create Template modal state
  const [openCreate, setOpenCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [tplType, setTplType] = useState<'prompt'|'persona'>('prompt')
  const [tplTitle, setTplTitle] = useState('')
  const [tplDesc, setTplDesc] = useState('')
  const [tplVis, setTplVis] = useState<'personal'|'public'>('personal')

  // Parse content with streaming-aware detection
  const parsedContent = useMemo(() => {
    // Process markdown for both assistant and tool messages
    if ((!isAssistant && !isTool) || !message.content) {
      return { processedContent: message.content || '', codeBlocks: [], hasIncompleteBlocks: false };
    }

    // If custom delimiters are disabled, return content as-is for standard markdown parsing
    if (customDelimitersEnabled === false) {
      return { processedContent: message.content, codeBlocks: [], hasIncompleteBlocks: false };
    }

    // If we haven't loaded config yet, return content as-is temporarily
    if (customDelimitersEnabled === null) {
      return { processedContent: message.content, codeBlocks: [], hasIncompleteBlocks: false };
    }

    // Use streaming parser during streaming for better performance
    if (isStreaming) {
      // Initialize parser state if needed
      if (!streamingParserStateRef.current) {
        streamingParserStateRef.current = createStreamingParserState();
      }

      // Calculate new content since last parse
      const previousContent = previousContentRef.current;
      const newContent = message.content.slice(previousContent.length);
      
      if (newContent) {
        const streamingResult = parseStreamingContent(newContent, streamingParserStateRef.current);
        streamingParserStateRef.current = streamingResult.parserState;
        previousContentRef.current = message.content;
        
        return {
          processedContent: streamingResult.processedContent,
          codeBlocks: streamingResult.codeBlocks,
          hasIncompleteBlocks: streamingResult.hasIncompleteBlocks
        };
      }

      // Return previous result if no new content
      return streamingParserStateRef.current ? {
        processedContent: streamingParserStateRef.current.buffer,
        codeBlocks: [
          ...streamingParserStateRef.current.completedBlocks,
          ...Array.from(streamingParserStateRef.current.activeCodeBlocks.values()).map(pb => ({
            id: pb.id!,
            language: pb.language!,
            content: pb.content || '',
            startIndex: pb.startIndex!,
            endIndex: pb.startIndex! + (pb.content?.length || 0),
            isComplete: false
          } as CodeBlock))
        ],
        hasIncompleteBlocks: streamingParserStateRef.current.activeCodeBlocks.size > 0
      } : { processedContent: message.content, codeBlocks: [], hasIncompleteBlocks: false };
    }

    // Use standard parser for completed messages
    streamingParserStateRef.current = null;
    previousContentRef.current = '';
    return parseCodeBlocks(message.content);
  }, [isAssistant, message.content, isStreaming, customDelimitersEnabled]);

  // Fetch configuration on component mount
  useEffect(() => {
    configService.isCustomDelimitersEnabled().then(setCustomDelimitersEnabled);
  }, []);

  // Reset parser state when streaming stops
  useEffect(() => {
    if (!isStreaming && streamingParserStateRef.current) {
      streamingParserStateRef.current = null;
      previousContentRef.current = '';
    }
  }, [isStreaming]);

  return (
    <div className={`flex gap-4 py-3 ${isUser ? 'justify-end' : 'justify-start'} ${
      !isUser ? 'bg-gradient-to-r from-gray-50/50 to-transparent dark:from-gray-800/50' : ''
    }`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${
            isAssistant 
              ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          }`}>
            {isAssistant ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />}
          </div>
        </div>
      )}

      <div className={`flex-1 max-w-[75%] ${isUser ? 'order-first' : ''}`}>
        <div className={`relative group rounded-xl p-4 ${message.content ? 'pr-4' : ''} shadow-sm ${
          isUser 
            ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' 
            : isSystem 
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' 
            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
        }`}>
          {/* Quick copy message button (top-right of bubble) */}
          {message.content && (isUser || isAssistant || isTool) && (
            <button
              type="button"
              onClick={handleCopyMessage}
              className={`absolute top-2 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
                bg-black/5 dark:bg-white/10 text-gray-500 dark:text-gray-200
                shadow-sm backdrop-blur-sm`}
              title="Copy message to clipboard"
            >
              {copiedMessage ? (
                <>
                  <Check className="w-3 h-3" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
          {isAssistant || isTool ? (
            <div className="space-y-4 mt-5">
              {/* New structured thinking process display */}
              {message.thinkingSteps && message.thinkingSteps.length > 0 && (
                <ThinkingPanel steps={message.thinkingSteps} isStreaming={isStreaming} />
                          )}
              
              {/* Render custom code blocks first */}
              {parsedContent.codeBlocks.length > 0 && (
                <div className="space-y-3">
                  {parsedContent.codeBlocks.map((codeBlock) => (
                    <EnhancedCodeBlock
                      key={codeBlock.id}
                      language={codeBlock.language}
                      isCustomDelimited={!codeBlock.id.startsWith('markdown-')}
                      isIncomplete={!codeBlock.isComplete && isStreaming}
                    >
                      {codeBlock.content}
                    </EnhancedCodeBlock>
                  ))}
                </div>
              )}

              {/* Render processed markdown content */}
              {parsedContent.processedContent && (
                <div className="prose prose-sm max-w-none dark:prose-invert break-words [overflow-wrap:anywhere]
                  prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-gray-100
                  prose-h1:text-2xl prose-h1:mb-6 prose-h1:border-b prose-h1:border-gray-200 dark:prose-h1:border-gray-700 prose-h1:pb-2
                  prose-h2:text-xl prose-h2:mb-4 prose-h2:text-emerald-700 dark:prose-h2:text-emerald-400
                  prose-h3:text-lg prose-h3:mb-3 prose-h3:text-emerald-600 dark:prose-h3:text-emerald-500
                  prose-p:leading-relaxed prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:break-words
                  prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold
                  prose-em:text-emerald-600 dark:prose-em:text-emerald-400
                  prose-ul:space-y-1 prose-ol:space-y-1
                  prose-li:text-gray-700 dark:prose-li:text-gray-300
                  prose-blockquote:border-l-4 prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-50 dark:prose-blockquote:bg-emerald-900/20
                  prose-blockquote:italic prose-blockquote:px-4 prose-blockquote:py-2
                  prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline prose-a:break-all
                  prose-table:w-full prose-table:border-collapse
                  prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:px-3 prose-th:py-2
                  prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-3 prose-td:py-2
                  prose-hr:border-gray-300 dark:prose-hr:border-gray-600">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight, rehypeRaw]}
                    components={{
                      img: MarkdownImg,
                      code: ({ node, inline, className, children, ...props }: any) => {
                        // Standard markdown code blocks (fallback)
                        return (
                          <CodeBlock
                            inline={inline}
                            className={className}
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </CodeBlock>
                        )
                      },
                      a: ({ href, children, ...props }: any) => {
                        // Check if this is a blob storage link (export document)
                        // IMPORTANT: Only treat OUR storage account as "blob storage" for proxying.
                        // Azure OpenAI image URLs can also contain "blob.core.windows.net" but are not hosted in our account.
                        // Proxying those can fail (redirects/expiry), and should not be forced through our proxy.
                        const isBlobStorage = href?.includes('giatecchatstorage.blob.core.windows.net')
                        
                        // Check if this is a secure generated document download link
                        const isGeneratedDocument = href?.includes('/api/files/generated/')

                        // For blob storage links, use backend proxy.
                        // IMPORTANT: In production, frontend and backend are on different hosts.
                        // Using a relative "/api/..." URL will hit the frontend nginx and return index.html (SPA fallback),
                        // which then gets downloaded as "proxy.html". Use the configured backend base URL instead.
                        const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000'
                        const downloadUrl = isBlobStorage
                          ? `${apiBaseUrl}/api/files/download/proxy?url=${encodeURIComponent(href)}`
                          : href

                        // Handle secure generated document downloads with authentication
                        const handleGeneratedDocumentClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
                          if (isGeneratedDocument) {
                            e.preventDefault()
                            try {
                              // Extract download_id from URL
                              const urlMatch = href.match(/\/api\/files\/generated\/([^/?]+)/)
                              if (!urlMatch) {
                                console.error('Failed to extract download_id from URL:', href)
                                return
                              }
                              const downloadId = urlMatch[1]
                              
                              // Use apiClient to download with authentication
                              const token = apiClient.getAuthToken()
                              const headers: HeadersInit = {}
                              if (token) {
                                headers.Authorization = `Bearer ${token}`
                              }
                              
                              const response = await fetch(`${apiBaseUrl}/api/files/generated/${downloadId}`, {
                                headers,
                              })
                              
                              if (!response.ok) {
                                if (response.status === 401) {
                                  throw new Error('Authentication required. Please login again.')
                                }
                                throw new Error(`Download failed: ${response.statusText}`)
                              }
                              
                              // Get filename from Content-Disposition header or use default
                              const contentDisposition = response.headers.get('Content-Disposition')
                              let filename = 'document'
                              if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                                if (filenameMatch) {
                                  filename = filenameMatch[1].replace(/['"]/g, '')
                                }
                              }
                              
                              // Download file
                              const blob = await response.blob()
                              const url = window.URL.createObjectURL(blob)
                              const a = document.createElement('a')
                              a.href = url
                              a.download = filename
                              document.body.appendChild(a)
                              a.click()
                              document.body.removeChild(a)
                              window.URL.revokeObjectURL(url)
                            } catch (error) {
                              console.error('Failed to download generated document:', error)
                              alert(error instanceof Error ? error.message : 'Failed to download document')
                            }
                          }
                        }

                        return (
                          <a
                            href={downloadUrl}
                            onClick={handleGeneratedDocumentClick}
                            // "download" attribute is ignored by most browsers for cross-origin URLs.
                            // Backend sets Content-Disposition: attachment, so the download should still work.
                            target={isBlobStorage ? "_blank" : "_blank"}
                            rel="noopener noreferrer"
                            className="break-all [overflow-wrap:anywhere]"
                            {...props}
                          >
                            {children}
                          </a>
                        )
                      },
                      h1: ({ children }) => (
                        <h1 className="flex items-center gap-2">
                          <span className="w-1 h-6 bg-emerald-500 rounded-full"></span>
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="flex items-center gap-2">
                          <span className="w-1 h-5 bg-emerald-500 rounded-full"></span>
                          {children}
                        </h2>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                          <table className="min-w-full">
                            {children}
                          </table>
                        </div>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="relative">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 rounded-full"></div>
                          <div className="pl-4">{children}</div>
                        </blockquote>
                      )
                    }}
                  >
                    {parsedContent.processedContent}
                  </ReactMarkdown>
                </div>
              )}

              {/* Show cursor when streaming */}
              {isStreaming && (message.content || parsedContent.hasIncompleteBlocks) && (
                <span className="inline-block w-1 h-4 bg-emerald-500 animate-pulse ml-1" />
              )}
            </div>
          ) : (
            <>
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.content}</p>
              
              {/* Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {message.attachments.map((attachment, index) => {
                    const att: any = attachment as any
                    const sizeBytes = Number(att.file_size || 0)
                    const downloadHref =
                      att.file_path ||
                      att.url ||
                      (att.file_id || att.id ? `/api/files/${att.file_id || att.id}` : undefined)

                    return (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg"
                      >
                        <File className="w-4 h-4" />
                        <span className="text-sm flex-1 truncate">{attachment.filename}</span>
                        <span className="text-xs opacity-70">
                          {(sizeBytes / 1024).toFixed(1)} KB
                        </span>
                        {downloadHref && (
                          <a
                            href={downloadHref}
                            download={attachment.filename}
                            target="_blank"
                            rel="noopener"
                            className="p-1 hover:bg-white/20 rounded"
                          >
                            <Download className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 mt-2">
          <p className="text-xs text-muted-foreground">
            {new Date(message.created_at).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>

          {isUser && message.content && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTplType('prompt')
                setTplTitle((message.content || '').slice(0, 30) || 'New Template')
                setTplDesc('')
                setTplVis('personal')
                setOpenCreate(true)
              }}
              className="h-7 px-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              + Create Template
            </Button>
          )}
        </div>
      </div>

      {isUser && (
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-md">
            <User className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Create Template modal */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent onOpenChange={setOpenCreate} className="sm:max-w-xl w-[92vw] p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Type</label>
              <select className="border rounded px-2 py-1 bg-background text-foreground" value={tplType} onChange={(e) => setTplType(e.target.value as any)}>
                <option value="prompt">prompt</option>
                <option value="persona">persona</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Title</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} maxLength={50} placeholder="Concise name (max 50)" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Description</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={tplDesc} onChange={(e) => setTplDesc(e.target.value)} maxLength={100} placeholder={tplType === 'prompt' ? 'Description is required for prompt' : 'Optional short description'} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Visibility</label>
              <select className="border rounded px-2 py-1 bg-background text-foreground" value={tplVis} onChange={(e) => setTplVis(e.target.value as any)}>
                <option value="personal">personal</option>
                <option value="public">public</option>
              </select>
            </div>
            <div className="flex items-start gap-2">
              <label className="text-sm w-28 mt-2">Content</label>
              <textarea className="border rounded px-2 py-2 flex-1 h-40 bg-background text-foreground" defaultValue={message.content} readOnly />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>Cancel</Button>
            <Button onClick={async () => {
              try {
                setCreating(true)
                await apiClient.createTemplate({
                  type: tplType,
                  title: tplTitle.substring(0, 50),
                  description: tplDesc.substring(0, 100) || undefined,
                  content: (message.content || '').substring(0, 20000),
                  visibility: tplVis,
                })
                setOpenCreate(false)
              } catch (e: any) {
                alert(e?.message || 'Failed to create template')
              } finally {
                setCreating(false)
              }
            }} disabled={creating || !tplTitle.trim() || !(message.content || '').trim() || (tplType === 'prompt' && !tplDesc.trim())}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

